import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
// components
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

export default function ReservationFormDialog({ open, onClose, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();

  const ReservationSchema = Yup.object().shape({
    fullName: Yup.string().required('Nome é obrigatório'),
    email: Yup.string().email('Email inválido').required('Email é obrigatório'),
    phoneNumber: Yup.string().required('Telefone é obrigatório'),
    personsAmmount: Yup.number()
      .min(1, 'Mínimo 1 pessoa')
      .max(50, 'Máximo 50 pessoas')
      .required('Número de pessoas é obrigatório'),
    date: Yup.date().required('Data é obrigatória'),
    notes: Yup.string(),
  });

  const defaultValues = {
    fullName: '',
    email: '',
    phoneNumber: '',
    personsAmmount: 2,
    date: null,
    notes: '',
  };

  const methods = useForm({
    resolver: yupResolver(ReservationSchema),
    defaultValues,
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await axios.post('/api/reservation', {
        fullName: data.fullName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        personsAmmount: data.personsAmmount,
        date: data.date.toISOString(),
        notes: data.notes || '',
      });
      enqueueSnackbar('Reserva criada com sucesso', { variant: 'success' });
      reset(defaultValues);
      onSuccess();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Ocorreu um erro', { variant: 'error' });
    }
  });

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>Nova Reserva</DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <RHFTextField name="fullName" label="Nome Completo" />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <RHFTextField name="email" label="Email" />
              <RHFTextField name="phoneNumber" label="Telefone" />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="date"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    label="Data e Hora"
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />

              <RHFTextField
                name="personsAmmount"
                label="Nº Pessoas"
                type="number"
                InputProps={{ inputProps: { min: 1, max: 50 } }}
              />
            </Stack>

            <RHFTextField
              name="notes"
              label="Notas"
              multiline
              rows={3}
              placeholder="Observações especiais, alergias, etc..."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Criar Reserva
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

ReservationFormDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};
